'use client';

import { useTouchPrimary } from '@/hooks/use-has-primary-touch';
import {
  getShortcut,
  isApplePlatform,
  resolveDisplayToken,
} from '@/lib/shortcuts';
import { cn } from '@/lib/utils';
import { CommandGroup, CommandItem } from '@/components/ui/command';
import type { ComponentProps, ComponentType, ReactNode } from 'react';

export function Kbd({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <kbd
      className={cn(
        'border-border/50 bg-muted/40 text-muted-foreground/70 text-2xs pointer-events-none inline-flex h-5 min-w-5 items-center justify-center rounded border px-1 font-mono font-medium select-none',
        className
      )}
    >
      {children}
    </kbd>
  );
}

/** Footer/input key chip, matching the composer @ picker's keys. */
export function MenuKey({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <Kbd
      className={cn(
        'text-muted-foreground text-2xs h-[18px] min-w-[18px] rounded-[5px] border-0 bg-[var(--soft-control-raised)] font-sans leading-none shadow-[var(--soft-control-raised-shadow)]',
        className
      )}
    >
      {children}
    </Kbd>
  );
}

/** Result group with the composer picker's quiet sentence-case heading. */
export function MenuGroup({
  className,
  ...props
}: ComponentProps<typeof CommandGroup>) {
  return (
    <CommandGroup
      className={cn(
        '[&_[cmdk-group-heading]]:text-muted-foreground px-2 py-0 [&_[cmdk-group-heading]]:flex [&_[cmdk-group-heading]]:h-8 [&_[cmdk-group-heading]]:items-center [&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:py-0 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:leading-none [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:normal-case',
        className
      )}
      {...props}
    />
  );
}

/** Result row matching the composer @ picker rows (44px, 12px radius). */
export function MenuItem({
  className,
  ...props
}: ComponentProps<typeof CommandItem>) {
  return (
    <CommandItem
      className={cn(
        // Concentric with the 20px panel: rows sit 8px in.
        'group text-foreground/90 data-[selected=true]:bg-hover-wash data-[selected=true]:text-foreground min-h-11 gap-3 rounded-[12px] text-sm leading-5 transition-colors duration-75 motion-reduce:transition-none',
        className
      )}
      {...props}
    />
  );
}

export function PrefixHint({
  prefix,
  label,
  onActivate,
}: {
  prefix: string;
  label: string;
  onActivate: (prefix: string) => void;
}) {
  return (
    <button
      type="button"
      tabIndex={-1}
      onMouseDown={(event) => {
        // Keep focus in the command input while applying the prefix.
        event.preventDefault();
        onActivate(prefix);
      }}
      className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-xs transition-colors"
    >
      <MenuKey className="pointer-events-auto cursor-pointer">{prefix}</MenuKey>
      <span>{label}</span>
    </button>
  );
}

export function ItemIcon({
  icon: Icon,
  className,
  iconClassName,
}: {
  icon: ComponentType<{ className?: string }>;
  className?: string;
  iconClassName?: string;
}) {
  return (
    <span
      className={cn(
        // A 28px raised soft circle, like the home skills list's icons and
        // the composer's +: no outline ring. On the selected row's wash it
        // lifts onto the panel tone, or it would match the wash and vanish.
        'text-muted-foreground flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[var(--soft-control-raised)] shadow-[var(--soft-control-raised-shadow)] group-data-[selected=true]:bg-[var(--soft-control-surface)]',
        className
      )}
    >
      <Icon className={cn('size-4', iconClassName)} />
    </span>
  );
}

/** Kbd chips for a registered shortcut; hidden on touch devices. */
export function ShortcutHint({ shortcutId }: { shortcutId: string }) {
  const isTouch = useTouchPrimary();
  const shortcut = getShortcut(shortcutId);
  if (!shortcut || isTouch) return null;
  const apple = isApplePlatform();
  return (
    <span className="ml-auto hidden shrink-0 items-center gap-0.5 sm:inline-flex">
      {shortcut.display.map((token, index) => (
        <MenuKey key={index}>{resolveDisplayToken(token, apple)}</MenuKey>
      ))}
    </span>
  );
}

export type RelativeTimeTranslator = (
  key: 'justNow' | 'daysAgo' | 'hoursAgo' | 'minutesAgo',
  values?: { count: number }
) => string;

export function formatRelativeTime(
  timestamp: number,
  t: RelativeTimeTranslator
): string {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return t('daysAgo', { count: days });
  if (hours > 0) return t('hoursAgo', { count: hours });
  if (minutes > 0) return t('minutesAgo', { count: minutes });
  return t('justNow');
}
